import React, { MouseEventHandler, PropsWithChildren, useRef, useState } from 'react';
import { useClickOutside } from '^/hooks';
import theme from '^/theme';
import styled, { CSSObject } from 'styled-components';
import { DropdownOption } from '^/types';

export const MenuWrapper = styled.div<{ triggerStyles?: CSSObject }>(({ triggerStyles }) => ({
  cursor: 'pointer',
  position: 'relative',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  height: '28px',
  aspectRatio: '1/1',
  borderRadius: '4px',
  backgroundColor: theme.colors.white[100],
  zIndex: 2,
  ':hover': {
    backgroundColor: theme.colors.lightBlue[75],
  },
  ...triggerStyles,
}));

const DropdownContent = styled.div<{ optionsWrapperStyles?: CSSObject }>(
  ({ optionsWrapperStyles }) => ({
    position: 'absolute',
    top: '24px',
    right: '0',
    backgroundColor: theme.colors.white[100],
    borderRadius: '4px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '8px 0px',
    width: '160px',
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...optionsWrapperStyles,
  })
);

const DropdownItem = styled.button({
  width: '100%',
  padding: '8px 16px',
  fontSize: '12px',
  color: theme.colorTokens.textCoolDark,

  backgroundColor: theme.colors.white[100],
  ':hover': {
    backgroundColor: theme.colors.lightBlue[75],
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  gap: '8px',
});

interface Props {
  options: DropdownOption[];
  triggerStyles?: CSSObject;
  optionsWrapperStyles?: CSSObject;
}

/**
 *
 * @description Pass the icon you want to show as the dropdown trigger as children. Rest is handled inside
 */
export function DropdownMenu({
  children,
  options,
  triggerStyles,
  optionsWrapperStyles,
}: PropsWithChildren<Props>) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    ref: dropdownRef,
    callback: () => setIsDropdownOpen(false),
  });

  const handleDropdownMenuClick: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleOptionClick = (option: DropdownOption) => {
    option.action();
    setIsDropdownOpen(false);
  };

  return (
    <MenuWrapper
      ref={dropdownRef}
      onClick={handleDropdownMenuClick}
      data-testid={`dropdown-menu`}
      triggerStyles={triggerStyles}
    >
      {children}
      {isDropdownOpen && (
        <DropdownContent optionsWrapperStyles={optionsWrapperStyles}>
          {options
            ?.filter(option => !option.hide)
            ?.map(option => (
              <DropdownItem
                data-testid={`dropdown-item-${option.label}`}
                key={option.label}
                onClick={() => handleOptionClick(option)}
              >
                <div>{option.preIcon}</div>
                {option.label}
              </DropdownItem>
            ))}
        </DropdownContent>
      )}
    </MenuWrapper>
  );
}
